import { ITEM_STATUS_CANCELLED, ITEM_STATUS_COMPLETED } from 'constants/cart'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { nonNullable, unique } from 'lib/array/arrayUtils'
import { convertTwelveHourTimeToTimeStr } from 'lib/datetime/time'
import { findByName, findByTwoLetterCode } from 'lib/insurance/insuranceCountries'
import getTourV2CountryCodesVisited from 'lib/tours/getTourV2CountryCodesVisited'

export interface PassportCardOrderQuoteItemSources {
  /** Cruise offer snapshots keyed by cruise booking id (state.cruise.cruiseOfferSnapshot). */
  cruiseOfferSnapshots: Record<string, App.Cruises.CruiseOfferSnapshot | undefined>
  /** Flight order item details keyed by flight order item id (getFlightDetailsByOrderItems). */
  flightDetailsByOrderItems: Record<string, App.FlightOrderItemDetails | undefined>
}

interface PassportCardTripAddress {
  street: string
  country: string
  zip: string
  city: string
}

interface OrderItemWithStatus {
  status: string
}

function isActiveItem(item: OrderItemWithStatus): boolean {
  return item.status !== ITEM_STATUS_CANCELLED
}

function isCompletedItem(item: OrderItemWithStatus): boolean {
  return item.status === ITEM_STATUS_COMPLETED
}

function toCountryNames(countryCodes: Array<string>): Array<string> {
  return countryCodes
    .map(code => findByTwoLetterCode(code))
    .filter((name): name is string => !!name)
}

/**
 * Cruise itinerary times arrive as "5:00 PM" or the "--" placeholder. PassportCard wants
 * HH:mm, and its time fields are optional, so unparseable values are passed through and
 * placeholders become empty.
 */
export function sanitizeCruiseTime(time: string | undefined): string {
  if (!time || time === '--') return ''
  const match = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!match) return time
  return convertTwelveHourTimeToTimeStr(`${match[1]}:${match[2]}${match[3].toUpperCase()}`)
}

/**
 * Countries visited in trip order. The itinerary arrives in arbitrary array order and the
 * ends of this list are read as the tour's departure and arrival country, so it is sorted by
 * day first.
 */
export function getTourCountryCodesInTripOrder(itinerary: Array<App.Tours.TourV2OfferItineraryItem>): Array<string> {
  return getTourV2CountryCodesVisited([...itinerary].sort((dayA, dayB) => dayA.startDay - dayB.startDay))
}

function getAccommodationTrips(order: App.Order): Array<App.PassportCardQuoteItem> {
  return order.items.flatMap((item): Array<App.PassportCardQuoteItem> => {
    const reservation = item.reservation
    if (!isActiveItem(item) || !reservation?.startDate || !reservation.endDate) return []

    const property = item.offer.property
    const geoData = property?.geoData
    const street = [geoData?.streetNumber, geoData?.route].filter(Boolean).join(' ').trim() || property?.address || ''
    const address: PassportCardTripAddress = {
      street,
      country: geoData?.countryCode ?? '',
      zip: '',
      city: geoData?.locality ?? '',
    }

    return [{
      dp: 'accommodation',
      checkin: reservation.startDate,
      checkout: reservation.endDate,
      accommodationType: 'HOTEL',
      name: item.offer.name,
      address,
    }]
  })
}

function getBedbankTrips(order: App.Order): Array<App.PassportCardQuoteItem> {
  return order.bedbankItems.filter(isActiveItem).map((item): App.PassportCardQuoteItem => {
    const propertyAddress = item.offer.property.address
    const address: PassportCardTripAddress = {
      street: [propertyAddress.line1, propertyAddress.line2].filter(Boolean).join(' ').trim(),
      country: propertyAddress.countryCode ?? '',
      zip: propertyAddress.postalCode ?? '',
      city: propertyAddress.city ?? '',
    }

    return {
      dp: 'accommodation',
      checkin: item.checkIn.format(ISO_DATE_FORMAT),
      checkout: item.checkOut.format(ISO_DATE_FORMAT),
      accommodationType: 'HOTEL',
      name: item.offer.name,
      address,
    }
  })
}

function getFlightTrips(
  order: App.Order,
  flightDetailsByOrderItems: PassportCardOrderQuoteItemSources['flightDetailsByOrderItems'],
): Array<App.PassportCardQuoteItem> {
  return order.flightItems.filter(isActiveItem).flatMap((item): Array<App.PassportCardQuoteItem> => {
    const legs = flightDetailsByOrderItems[item.id]?.bookingJourney?.legs ?? []
    const flights = legs.flatMap(leg => leg.flights ?? []).map(segment => ({
      departureDate: segment.departingDate,
      departureTime: segment.departingTime,
      arrivalDate: segment.arrivalDate,
      arrivalTime: segment.arrivalTime,
      flightId: segment.flightNumber,
      departureAirport: segment.departureAirport,
      arrivalAirport: segment.arrivalAirport,
    }))
    if (!flights.length) return []

    return [{ dp: 'flight', flights }]
  })
}

function getTourTrips(order: App.Order): Array<App.PassportCardQuoteItem> {
  return order.tourItems.filter(isCompletedItem).flatMap((item): Array<App.PassportCardQuoteItem> => {
    const itineraryCountries = unique(toCountryNames(getTourCountryCodesInTripOrder(item.tour.itinerary ?? [])))
    // Older snapshots have no per-day location details but always list the countries visited.
    // That list mixes localities in ("Manly", "The Beach"), and PassportCard rejects a trip
    // whose destinations it cannot resolve to a country - so keep only real countries, the
    // same way getInsuranceDestinationCountriesFromOrder reads this field.
    const destinations = itineraryCountries.length
      ? itineraryCountries
      : unique(nonNullable(item.tour.countriesVisited.map(country => findByName(country)?.name)))
    const departureCountry = destinations[0]
    const arrivalCountry = destinations[destinations.length - 1]
    // PassportCard rejects an empty departureCountry outright: drop a trip we cannot
    // describe rather than send a request that cannot succeed.
    if (!departureCountry || !arrivalCountry) return []

    return [{
      dp: 'tour',
      name: item.tour.title,
      departureCountry,
      arrivalCountry,
      departureDate: item.tour.startDate,
      arrivalDate: item.tour.endDate,
      destinations,
    }]
  })
}

function getCruiseTrips(
  order: App.Order,
  cruiseOfferSnapshots: PassportCardOrderQuoteItemSources['cruiseOfferSnapshots'],
): Array<App.PassportCardQuoteItem> {
  return order.cruiseItems.filter(isCompletedItem).flatMap((item): Array<App.PassportCardQuoteItem> => {
    const snapshot = cruiseOfferSnapshots[item.bookingId]
    if (!snapshot) return []

    const portDays = snapshot.itineraries
      .filter(day => !!day.port)
      .sort((dayA, dayB) => dayA.dayNumber - dayB.dayNumber)
    const firstPortDay = portDays[0]
    const lastPortDay = portDays[portDays.length - 1]
    const departureCountry = findByTwoLetterCode(firstPortDay?.port?.countryCode ?? '')
    const arrivalCountry = findByTwoLetterCode(lastPortDay?.port?.countryCode ?? '')
    const destinations = unique(toCountryNames(portDays.map(day => day.port!.countryCode)))
    if (!departureCountry || !arrivalCountry || !destinations.length) return []

    return [{
      dp: 'cruise',
      name: snapshot.offerName,
      departureCountry,
      arrivalCountry,
      departureDate: item.departureDate,
      departureTime: sanitizeCruiseTime(firstPortDay.departureTime),
      arrivalDate: item.arrivalDate,
      arrivalTime: sanitizeCruiseTime(lastPortDay.arrivalTime),
      destinations,
    }]
  })
}

/**
 * PassportCard trip items ("DPs") for an existing order, used when the checkout cart
 * carries no trip items of its own (post-purchase insurance). Mirrors the cart mapper in
 * checkout/selectors/view/itemViews/getPassportCardQuoteItems: same vertical order, same
 * country resolution, and the same rule of dropping a trip that cannot be described.
 */
export function getPassportCardQuoteItemsFromOrder(
  order: App.Order,
  { cruiseOfferSnapshots, flightDetailsByOrderItems }: PassportCardOrderQuoteItemSources,
): Array<App.PassportCardQuoteItem> {
  return [
    ...getAccommodationTrips(order),
    ...getBedbankTrips(order),
    ...getFlightTrips(order, flightDetailsByOrderItems),
    ...getTourTrips(order),
    ...getCruiseTrips(order, cruiseOfferSnapshots),
  ]
}
